<template>
    <div class="accordion-multi-select" @click="handleClick" >
        <v-select :options="columns" v-model="value_obj"
            :searchable="true" class="searchable-select v-multi-select"
            :selectable="(option) => option.text != '' && option.text != 'CATEGORY DIVIDER'"
            label="text" @search="fetchOptions"
            @update:modelValue="emitInput"  multiple
            :filterable="false"
            :disabled="!if_allow_edit"
            >
            <template #selected-option="{ text, category, subcategory }">
                <span :option-selected="text" 
                      data-bs-toggle="tooltip"
                      :data-bs-original-title="text"> 
                    {{(category != null && category != "" && category != "list-header" ? category + ": " : "")}}
                    {{subcategory != null && subcategory != "" ? subcategory + " › " : ""}}
                    {{text}}
                </span>
            </template>

            <template #open-indicator="{ attributes }">
                <span v-bind="attributes" style="width: 12px; line-height: 8px;">
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'>
                        <path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/>
                    </svg>
                </span>
            </template>

            <template #option="{ text, category, subcategory, extra }">
                <div v-if="text == '' && !subcategory" :data-category="category" class="category-header"
                    @click="expandCategory(category, null)"
                    @mouseover="handleHover($event)"
                    data-isexpanded="false">
                    {{ category }} <i class="fas fa-caret-right"></i>
                </div>
                <div v-else-if="text == '' && subcategory"
                    :data-subcategory="category"
                    :data-subcategory-name="subcategory"
                    class="subcategory-header"
                    @click="expandSubcategory(category, subcategory, null)"
                    @mouseover="handleHover($event)"
                    data-isexpanded="false"
                    v-show="isSubcategoryVisible(category, subcategory)">
                    {{ subcategory }} <i class="fas fa-caret-right"></i>
                </div>
                <div v-else class="suboption" :data-subcategory="category" :data-subcategory-name="subcategory"
                    :class="shouldShowOption(category, subcategory)"
                    @mouseover="handleHover($event)"
                    :title="text">
                    {{getDisplayText(text, category, subcategory)}} <span v-html="extra" v-if='extra != null'></span>
                </div>
            </template>
            <template #no-options="{ search, searching, loading }">
                <div class="suboption show" v-if="is_loading">
                    <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> <span class="visually-hidden">Loading...</span></div>  Loading columns
                </div>
                <div class="suboption show" v-else>
                    <em>No results found</em>
                </div>
            </template>
        </v-select>
        <input type="hidden" :name="field_name" :value="(value_obj == null ? [null] : value_obj.value)" />
    </div>
</template>

<script>
import vSelect from "vue3-select";

export default {
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        options: {
            type: Array,
            default: () => []
        },
        field_name: {
            type: String,
            default: 'vue-multi-select',
        },
        show_hidden_fields: {
            type: Boolean,
            default: false,
        },
        max_num_values: {
            default: 0,
        },
        if_allow_edit: {
            type: Boolean,
            default: true
        }
    },
    components: {
        vSelect
    },
    data() {
        return {
            value_obj: [],
            columns: [],
            orig_columns: [],
            is_loading: false,
            expanded_categories: [],
            expanded_subcategories: [],
        };
    },
    mounted() {
        this.orig_columns = JSON.parse(JSON.stringify(this.options));
        this.columns = this.formatOptions();
        this.loadSelectedOptions();
        this.initializeTooltips();
    },
    updated() {
        this.initializeTooltips();
    },
    watch:{
        modelValue: {
            handler(newValue) {
                this.loadSelectedOptions();
            }
        },
        options: {
            handler(newValue) {
                this.orig_columns = JSON.parse(JSON.stringify(this.options));
                this.loadSelectedOptions();
            }
        }
    },
    methods: {
        initializeTooltips() {
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            for (var i = 0; i < tooltipTriggerList.length; i++) {
              (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                  // Hide the tooltip using the index passed to the closure
                  tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                  // Hide the tooltip using the index passed to the closure
                  tooltipList[index].hide();
                });
              })(i);
            }
        },
        handleClick() {
          this.$emit('editing');
        },
        addHeaders() {
            var current_category = null;
            var current_subcategory = null;

            // Sort columns to ensure proper nesting
            this.columns.sort((a, b) => {
                const aValue = String(a.value);
                const bValue = String(b.value);
                const aCategory = a.category || (aValue.indexOf(".") != -1 ? aValue.split(".")[0] : null);
                const bCategory = b.category || (bValue.indexOf(".") != -1 ? bValue.split(".")[0] : null);
                if (aCategory && bCategory) {
                    const categoryCompare = aCategory.localeCompare(bCategory);
                    if (categoryCompare !== 0) return categoryCompare;
                } else if (aCategory && !bCategory) {
                    return -1;
                } else if (!aCategory && bCategory) {
                    return 1;
                }
                if(aValue.indexOf(".") == -1 && bValue.indexOf(".") != -1)
                    return -1;
                else if(aValue.indexOf(".") != -1 && bValue.indexOf(".") == -1)
                    return 1;
                if(aValue.indexOf(".") != -1 && bValue.indexOf(".") != -1) {
                    const aParts = aValue.split(".");
                    const bParts = bValue.split(".");
                    if(aParts[0] === bParts[0]) {
                        if(aParts.length === 2 && bParts.length > 2) return -1;
                        if(aParts.length > 2 && bParts.length === 2) return 1;
                        if(aParts.length > 2 && bParts.length > 2) {
                            return aParts[1].localeCompare(bParts[1]);
                        }
                    }
                    return aParts[0].localeCompare(bParts[0]);
                }
                return aValue.localeCompare(bValue);
            });

            for(let i = 0; i < this.columns.length; i++) {
                let col = this.columns[i];
                let category = null;
                let subcategory = null;
                let text = col.text;
                const colValue = String(col.value);
                if(colValue.indexOf("CASE WHEN") != -1 && col.category && col.category.trim() != '') {
                    category = this.propercase(col.category);
                    text = col.text;
                    subcategory = null;
                    if(category != current_category) {
                        this.columns.splice(i, 0, {
                            value: "DIVIDER-"+ category,
                            category: category,
                            text: "",
                            subcategory: null
                        }); 
                        current_subcategory = null;
                        i++;
                    }
                }
                if (col.category && col.category.trim() != '') {
                    category = this.propercase(col.category);
                    text = col.text;
                    if (col.subcategory && col.subcategory.trim() !== '') {
                        subcategory = this.propercase(col.subcategory);
                    } else if (colValue.indexOf(".") !== -1) {
                        const parts = colValue.split(".");
                        if (parts.length > 2) {
                            subcategory = this.propercase(parts[1]);
                        }
                    } else {
                        subcategory = null;
                    }
                    if(category != current_category) {
                        this.columns.splice(i, 0, {
                            value: "DIVIDER-"+ category,
                            category: category,
                            text: "",
                            subcategory: null
                        });
                        current_subcategory = null;
                        i++;
                    }
                    if(subcategory && subcategory != current_subcategory) {
                        this.columns.splice(i, 0, {
                            value: "DIVIDER-"+ category + "-" + subcategory,
                            category: category,
                            subcategory: subcategory,
                            text: ""
                        });
                        i++;
                    }
                } else if(colValue.indexOf(".") != -1) {
                    let parts = colValue.split(".");
                    category = this.propercase(parts[0]);
                    if(parts.length > 2) {
                        subcategory = this.propercase(parts[1]);
                        text = this.propercase(parts.slice(2).join(' › '));
                    } else {
                        text = this.propercase(parts[1]);
                    }
                    if(category != current_category) {
                        this.columns.splice(i, 0, {
                            value: "DIVIDER-"+ category,
                            category: category,
                            text: "",
                            subcategory: null
                        });
                        current_subcategory = null;
                        i++;
                    }
                    if(subcategory && subcategory != current_subcategory) {
                        this.columns.splice(i, 0, {
                            value: "DIVIDER-"+ category + "-" + subcategory,
                            category: category,
                            subcategory: subcategory,
                            text: ""
                        });
                        i++;
                    }
                }
                current_category = category;
                current_subcategory = subcategory;
                this.columns[i] = {
                    ...col,
                    category: category,
                    subcategory: subcategory,
                    text: text
                };
            }
            this.columns.sort((a, b) => {
                if (a.category !== b.category) {
                    if (!a.category && b.category) return -1;
                    if (a.category && !b.category) return 1;
                    if (a.category && b.category) {
                        return a.category.localeCompare(b.category);
                    }
                }
                if (a.subcategory !== b.subcategory) {
                    if (!a.subcategory && b.subcategory) return -1;
                    if (a.subcategory && !b.subcategory) return 1;
                    if (a.subcategory && b.subcategory) {
                        return a.subcategory.localeCompare(b.subcategory);
                    }
                }
                const aText = a.text || '';
                const bText = b.text || '';
                return aText.localeCompare(bText);
            });
        },
        formatOptions(){
            var options_list = [];
            var old_category = null;
            var old_main_category = null;
            var main_categories = new Set();
            this.orig_columns.forEach(option =>{
                if(!this.show_hidden_fields && (option.value != null && typeof option.value === 'string' && (option.value.indexOf("_") == 0 || option.value.indexOf("._") >= 0)))
                    return;
                let key = option.main_category == "Columns" ? option.value: option.id;
                let text = option.main_category == "Columns" || option.text ? option.text: option.name;
                let type = option.type;
                let category = option.category;
                let main_category = option.main_category;
                options_list.push({
                    main_category: main_category,
                    category: category,
                    type: type,
                    value: key,
                    text: text
                });
            });
            if (main_categories.size === 1) {
                options_list = options_list.filter(option => option.text !== "CATEGORY DIVIDER");
            }
            this.columns = options_list;
            this.addHeaders();
            return this.columns;
        },
        emitInput() {
            if(this.value_obj == null)
                this.$emit('update:modelValue', null);
            else{
                //If the max number of values is set, and the value_obj is at the max then just remove the second to last element
                if(this.max_num_values >0 && this.value_obj.length == this.max_num_values+1)
                    this.value_obj.splice(this.value_obj.length-2, 1);

                this.$emit('update:modelValue', this.value_obj);
            }
        },
        loadSelectedOptions(){
            var values = (this.modelValue && Array.isArray(this.modelValue)) ? this.modelValue : [this.modelValue];

            //Reset the array
            this.columns = this.formatOptions();

            if(values != null && values.length != 0 && values!=''){
                var value_obj_array = []
                values.forEach(value_obj => {
                    var value_object = null;

                    if (typeof value_obj == 'object'){
                        // Try to find by value/id property
                        const valueToMatch = value_obj.value || value_obj.id;

                        // First try to match by value and main_category if available
                        if (value_obj.main_category) {
                            value_object = this.columns.find(option =>
                                (option.value == valueToMatch) &&
                                (option.main_category == value_obj.main_category));
                        }

                        // If not found, try just by value/id
                        if (!value_object) {
                            value_object = this.columns.find(option => option.value == valueToMatch);
                        }

                        // If still not found and we have both id and value properties, try matching by id
                        if (!value_object && value_obj.id && value_obj.id !== value_obj.value) {
                            value_object = this.columns.find(option => option.value == value_obj.id);
                        }
                    } else { // value_obj is a string or number
                        value_object = this.columns.find(option => option.value == value_obj);
                    }

                    // If it found the object in the list of options...
                    if (value_object != null) {
                        value_obj_array.push(value_object);
                    } else if (typeof value_obj == 'object' && value_obj.text && value_obj.value) {
                        // If we couldn't find it in options but it has text/value properties, use it directly
                        // This handles cases where the selected value might not be in the options list
                        value_obj_array.push(value_obj);
                    }
                })
                this.value_obj = value_obj_array;
            } else {
                this.value_obj = null
            }
        },
        expandCategory(cat, expand) {
            if (!cat) return false;
            
            const isExpanded = this.expanded_categories.includes(cat);
            const shouldExpand = expand === true || !isExpanded;

            // Update state arrays immediately for reactive updates
            if (shouldExpand && !this.expanded_categories.includes(cat)) {
                this.expanded_categories.push(cat);
            } else if (!shouldExpand) {
                this.expanded_categories = this.expanded_categories.filter(c => c !== cat);
                // Remove all subcategories of this category
                this.expanded_subcategories = this.expanded_subcategories.filter(sc => !sc.startsWith(cat + '-'));
            }

            // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
            this.$nextTick(() => {
                // Find all elements related to this category
                const categoryHeader = document.querySelector(`div[data-category='${cat}']`);
                if (!categoryHeader) return;

                // Update category header
                const categoryIcon = categoryHeader.querySelector('.fa-caret-right, .fa-caret-down');
                if (categoryIcon) {
                    categoryIcon.classList.toggle('fa-caret-right', !shouldExpand);
                    categoryIcon.classList.toggle('fa-caret-down', shouldExpand);
                }
                categoryHeader.dataset.isexpanded = shouldExpand;

                // Find and update all related subcategory headers
                const subcategoryHeaders = document.querySelectorAll(`div[data-subcategory='${cat}'].subcategory-header`);
                subcategoryHeaders.forEach(header => {
                    header.style.display = shouldExpand ? "block" : "none";
                    if (!shouldExpand) {
                        header.dataset.isexpanded = "false";
                        const subcategoryIcon = header.querySelector('.fa-caret-right, .fa-caret-down');
                        if (subcategoryIcon) {
                            subcategoryIcon.classList.add('fa-caret-right');
                            subcategoryIcon.classList.remove('fa-caret-down');
                        }
                    }
                });
            });

            return false;
        },
        fetchOptions(search, loading) {
            //Reset the array
            this.columns = this.formatOptions();

            if(search == "") {
                // Reset expanded states when no search
                this.expanded_categories = [];
                this.expanded_subcategories = [];
                return;
            }

            this.is_loading = true;

            //Look at each column
            for(var i = this.columns.length-1; i >= 0 ; i--) {
                const item = this.columns[i];
                
                // Check if search matches text, category, or subcategory
                const searchLower = search.toLowerCase();
                const textMatch = item.text && item.text.toLowerCase().indexOf(searchLower) !== -1;
                const categoryMatch = item.category && item.category.toLowerCase().indexOf(searchLower) !== -1;
                const subcategoryMatch = item.subcategory && item.subcategory.toLowerCase().indexOf(searchLower) !== -1;
                
                // Keep item if it matches search in any field, or if it's a category/subcategory divider
                if (!textMatch && !categoryMatch && !subcategoryMatch && item.text !== "" && item.text !== "CATEGORY DIVIDER") {
                    this.columns.splice(i, 1);
                }
            }

            //Get the remaining categories and subcategories
            var cats = [];
            var subcats = new Map(); // Map to track subcategories and their matching options
            
            for(var i = 0; i < this.columns.length; i++) {
                const item = this.columns[i];
                if(item.category != null && item.text != "" && item.text != "CATEGORY DIVIDER") {
                    if(!cats.includes(item.category)) {
                        cats.push(item.category);
                    }
                    
                    // Track subcategories that have matching options
                    if(item.subcategory) {
                        const key = `${item.category}-${item.subcategory}`;
                        if(!subcats.has(key)) {
                            subcats.set(key, false);
                        }
                        subcats.set(key, true);
                    }
                }
            }

            //Remove subcategory headers that have no matching options
            for(var i = this.columns.length-1; i >= 0 ; i--) {
                const item = this.columns[i];
                if(item.text == "" && item.subcategory) {
                    const key = `${item.category}-${item.subcategory}`;
                    if(!subcats.get(key)) {
                        this.columns.splice(i, 1);
                    }
                }
            }

            //Remove category headers that have no matching options
            for(var i = this.columns.length-1; i >= 0 ; i--) {
                const item = this.columns[i];
                if(item.text == "" && item.category != null && !cats.includes(item.category)) {
                    this.columns.splice(i, 1);
                }
            }

            //Expand the categories and subcategories
            for(var i = 0; i < cats.length; i++) {
                this.expandCategory(cats[i], true);
                // Find and expand all subcategories for this category
                subcats.forEach((hasMatches, key) => {
                    const [category, subcategory] = key.split('-');
                    if (category === cats[i] && hasMatches) {
                        this.expandSubcategory(category, subcategory, true);
                    }
                });
            }

            this.is_loading = false;
            return true;
        },
        handleHover(event) {
            // Remove selected and highlight classes from all options
            const allOptions = document.querySelectorAll('.vs__dropdown-option');
            allOptions.forEach(option => {
                option.classList.remove('vs__dropdown-option--selected', 'vs__dropdown-option--highlight');
            });
        },
        expandSubcategory(cat, subcat, expand) {
            if (!cat || !subcat) return false;

            const subcategoryKey = `${cat}-${subcat}`;
            const isExpanded = this.expanded_subcategories.includes(subcategoryKey);
            const shouldExpand = expand === true || !isExpanded;

            // Update state array immediately for reactive updates
            if (shouldExpand && !this.expanded_subcategories.includes(subcategoryKey)) {
                this.expanded_subcategories.push(subcategoryKey);
            } else if (!shouldExpand) {
                this.expanded_subcategories = this.expanded_subcategories.filter(sc => sc !== subcategoryKey);
            }

            // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
            this.$nextTick(() => {
                const header = document.querySelector(`div[data-subcategory='${cat}'][data-subcategory-name='${subcat}']`);
                if (!header) return;

                // Update header
                const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                if (icon) {
                    icon.classList.toggle('fa-caret-right', !shouldExpand);
                    icon.classList.toggle('fa-caret-down', shouldExpand);
                }
                header.dataset.isexpanded = shouldExpand;

                // Find and update all options under this subcategory
                const options = document.querySelectorAll(`div[data-subcategory='${cat}'][data-subcategory-name='${subcat}'] + div.suboption`);
                options.forEach(option => {
                    option.style.display = shouldExpand ? "block" : "none";
                });
            });

            return false;
        },
        shouldShowOption(category, subcategory) {
            let classes = [];
            
            if (!category) {
                classes.push('show');
            } else {
                classes.push('category-option');
                const categoryExpanded = this.expanded_categories.includes(category);
                if (!categoryExpanded) return classes.join(' ');
                
                if (!subcategory) {
                    classes.push('show');
                } else {
                    classes.push('subcategory-option');
                    const subcategoryKey = `${category}-${subcategory}`;
                    if (this.expanded_subcategories.includes(subcategoryKey)) {
                        classes.push('show');
                    }
                }
            }
            
            return classes.join(' ');
        },
        isSubcategoryVisible(category, subcategory) {
            // On initial load, subcategory should only be visible if its parent category is expanded
            // If the category is not expanded, hide all its subcategories
            return this.expanded_categories.includes(category);
        },
        getDisplayText(text, category, subcategory) {
            if (!text) return '';
            
            // If there's no hierarchy context, don't strip anything
            if (!category && !subcategory) return text;
            
            // Try different possible separators
            const separators = [' › ', ' > ', ' - ', ': '];
            for (const separator of separators) {
                if (text.includes(separator)) {
                    const parts = text.split(separator);
                    return parts.pop();
                }
            }
            
            return text;
        },
    }
}
</script>

